import React from 'react';
import { Target, Eye, Users, Award } from 'lucide-react';

const Nosotros = () => {
  const valores = [
    {
      icono: Target,
      titulo: 'Misión',
      descripcion: 'Impulsar el desarrollo económico y laboral de Yucatán mediante políticas públicas eficientes que promuevan la generación de empleos dignos y el crecimiento empresarial sostenible.'
    },
    {
      icono: Eye,
      titulo: 'Visión',
      descripcion: 'Ser la secretaría líder en la región sureste que facilita oportunidades laborales de calidad y fomenta un ecosistema empresarial competitivo e innovador.'
    },
    {
      icono: Users,
      titulo: 'Compromiso Social',
      descripcion: 'Trabajamos de la mano con la ciudadanía yucateca para construir un futuro laboral inclusivo, equitativo y lleno de oportunidades para todos los sectores.'
    },
    {
      icono: Award,
      titulo: 'Excelencia',
      descripcion: 'Nos esforzamos por brindar servicios de alta calidad con transparencia, eficiencia y un enfoque centrado en las necesidades de la población y las empresas.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80" 
            alt="Quiénes Somos"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/70" />
        </div>
        <div className="relative max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <h1 className="text-5xl font-bold text-white mb-4">Quiénes Somos</h1>
          <p className="text-xl text-white/90 max-w-3xl">
            Conoce la Secretaría de Economía y Trabajo de Yucatán y nuestro compromiso con el desarrollo del estado.
          </p>
        </div>
      </div>

      {/* About Section */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Secretaría de Economía y Trabajo
            </h2>
            <div className="space-y-4 text-gray-600 leading-relaxed">
              <p>
                La Secretaría de Economía y Trabajo del Estado de Yucatán (SETY) es el organismo gubernamental encargado de diseñar, implementar y evaluar políticas públicas orientadas al fortalecimiento del desarrollo económico y laboral en la entidad.
              </p>
              <p>
                Bajo el marco del programa <strong>"Renacimiento Maya 2024-2030"</strong>, trabajamos para posicionar a Yucatán como un referente nacional e internacional en materia de empleo de calidad, innovación empresarial y desarrollo sostenible.
              </p>
              <p>
                Nuestra labor se centra en vincular a los sectores productivos con oportunidades de crecimiento, facilitar la inserción laboral de la población yucateca y crear un entorno favorable para la inversión y el emprendimiento.
              </p>
            </div>
          </div>

          <div className="rounded-xl overflow-hidden shadow-xl border border-gray-200">
            <img
              src="https://customer-assets.emergentagent.com/job_gobierno-moderno/artifacts/xlykdgrl_f8c616e17d00e1491ebd364c020019a5.jpg"
              alt="SETY Yucatán"
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Valores Section */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Nuestros Valores</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Los principios que guían nuestro trabajo diario en beneficio de la población yucateca
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {valores.map((valor, index) => {
              const IconComponent = valor.icono;
              return (
                <div
                  key={index}
                  className="bg-white rounded-xl p-8 border border-gray-200 hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-[#8B1538] to-[#6B0F2A] rounded-lg flex items-center justify-center mb-4">
                    <IconComponent className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {valor.titulo}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {valor.descripcion}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Stats Section */}
        <div className="bg-gradient-to-br from-slate-100 to-gray-200 rounded-xl overflow-hidden border border-gray-200">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="p-12">
              <div className="text-center lg:text-left mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Nuestro Impacto</h2>
                <p className="text-gray-600">Cifras que reflejan nuestro compromiso con Yucatán</p>
              </div>
              
              <div className="grid grid-cols-1 gap-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#8B1538] to-[#6B0F2A] rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-gray-900">15,000+</div>
                    <div className="text-gray-600">Empleos Generados</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#8B1538] to-[#6B0F2A] rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-gray-900">1,200+</div>
                    <div className="text-gray-600">Empresas Apoyadas</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#8B1538] to-[#6B0F2A] rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-gray-900">50+</div>
                    <div className="text-gray-600">Programas Activos</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="hidden lg:flex items-center justify-center p-12 bg-white">
              <img 
                src="https://illustrations.popsy.co/amber/team-work.svg" 
                alt="Team Work Illustration"
                className="w-full max-w-md"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Nosotros;